import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from "./audioPlayer.module.css"
import { useWavesurfer } from '@wavesurfer/react'
import { formatSecondsToTime } from '../../../utils/formatter'
import { MdOutlinePause, MdPlayArrow } from 'react-icons/md'
import CircleLoading from './../../CircleLoading'
import MediaUnavailable from '../../Chat/Elements/MediaUnavailable'

const AudioPlayer = ({ src, peaks, preload, style, pink = true, contrast = false, onPlay = () => { } }) => {
    const containerRef = useRef(null)
    const playNotifiedRef = useRef(false)
    const [waveSurferError, setWaveSurferError] = useState(false)
    const [loading, setLoading] = useState(true)
    const themeColors = useMemo(() => {
        const rootStyles = getComputedStyle(document.documentElement)
        const getColor = (name) => rootStyles.getPropertyValue(name).trim()

        return {
            wave: getColor('--text-secondary') || '#a7a7b4',
            primary: getColor('--brand-primary') || '#6245ae',
            secondary: getColor('--brand-secondary') || '#d2417c',
            contrastWave: 'rgba(255, 255, 255, 0.48)',
            contrastProgress: '#ffffff',
        }
    }, [])

    const { wavesurfer, isPlaying, currentTime } = useWavesurfer({
        container: containerRef,
        height: 20,
        waveColor: contrast ? themeColors.contrastWave : themeColors.wave,
        progressColor: contrast ? themeColors.contrastProgress : (pink ? themeColors.secondary : themeColors.primary),
        barWidth: 2,
        barGap: 2,
        barRadius: 3,
        url: src,
        peaks: peaks
    })

    const tooglePlayPause = useCallback(() => {
        wavesurfer && src && wavesurfer.playPause()
    }, [wavesurfer])

    useEffect(() => {
        playNotifiedRef.current = false
        setWaveSurferError(false)
        setLoading(true)
    }, [src])

    useEffect(() => {
        if (isPlaying && !playNotifiedRef.current) {
            playNotifiedRef.current = true
            onPlay()
        }
    }, [isPlaying, onPlay])

    useEffect(() => {
        if (wavesurfer) {
            const onError = () => {
                setWaveSurferError(true)
                setLoading(false)
            }
            const onDecode = () => {
                setWaveSurferError(false)
                setLoading(false)
            }

            wavesurfer.on('error', onError)
            wavesurfer.on('decode', onDecode)

            return () => {
                wavesurfer.un('error', onError)
                wavesurfer.un('decode', onDecode)
            }
        }
    }, [wavesurfer])

    const retryAudio = async () => {
        if (!wavesurfer || !src) return

        setWaveSurferError(false)
        setLoading(true)
        try {
            await wavesurfer.load(src)
        } catch (err) {
            setWaveSurferError(true)
            setLoading(false)
        }
    }

    return (
        <>
            <div className={`${styles.container} ${contrast ? styles.contrast : ''} ${waveSurferError ? styles.hidden : ''}`} style={style}>
                <div className={styles.controls} onClick={() => !loading && tooglePlayPause()}>
                    {isPlaying ? <MdOutlinePause /> : <MdPlayArrow />}
                </div>
                <div className={`${styles.audio} ${loading ? styles.hidden : ''}`} ref={containerRef}></div>
                {loading &&
                    <div className={`${styles.loading} ${contrast ? styles.contrast : ''} ${pink ? '' : styles.purple}`}>
                        <CircleLoading className={styles.icon} />
                    </div>
                }
                <div className={styles.time}><span>{formatSecondsToTime(currentTime, false)}</span></div>
            </div>
            {waveSurferError && <MediaUnavailable type="audio" onRetry={retryAudio} />}
        </>
    )
}

export default AudioPlayer
